import { useEffect, useCallback, useState } from "react";
import { TPeopleData, TPerson, TTeam, TTeamsData } from "../../BehaviouralComparison.types";
import { difference, differenceBy, keyBy, merge, values } from "lodash";
import { generateID, useAuth, useInputDebounce } from "utils";
import {
    BCIndividualsRefResponse,
    BCTeamsRefResponse,
    BCTeamsRequestResponse,
    createPaceDashboardTeams,
    deletePaceDashboardUsersFromTeams,
    getPaceDashboardIndividualsRef,
    getPaceDashboardTeamsRef,
    updatePaceDashboardTeams,
} from "services/pace/dashboard.service";
import { useAppDispatch } from "app/hooks";
import { setToastConfig } from "store/slice/UI";
import { convertIndividualsRef } from "pages/BehavioralComparison/adapters/converter.individualsRef";
import { TList } from "pages/BehavioralComparison/useBehaviouralComparison";
import { convertTeamsRef } from "pages/BehavioralComparison/adapters/converter.teamsRef";
import { RequestInfo } from "store/slice/store.types";
import { Pagination } from "types/global";
import { useSnackBar } from "components/SnackBar";
import { AxiosResponse } from "axios";

type TMode = "ADD" | "EDIT" | undefined;

const defaultMetaParams = {
    page_size: 32,
    page_no: 1,
};

const useTeamsModal = () => {
    const dispatch = useAppDispatch();
    const { loggedInUserData } = useAuth();

    const orgId = loggedInUserData.selectedOrg?.id;

    const [mode, setMode] = useState<TMode>(undefined);
    const [requestStatus, setRequestStatus] = useState<RequestInfo>("pristine");

    const [inputNameValue, setInputNameValue] = useState<string>("");
    const [inputTeamValue, setInputTeamValue] = useState<string>("");
    const [expandSelected, setExpandSelected] = useState<boolean>(false);

    const [people, setPeople] = useState<TPeopleData>({ meta: defaultMetaParams, status: "pristine" } as TPeopleData);

    const isAllPeopleSelected = people.data?.every((person) => person.checked);
    const isSomePeopleSelected = Boolean(
        people.data?.filter((person) => person.checked).length > 0 &&
            people.data?.filter((person) => person.checked).length < people.data.length
    );

    const [teams, setTeams] = useState<TTeamsData>({ meta: defaultMetaParams } as TTeamsData);

    const filteredTeams = teams.data?.filter((team) => team.name.toLowerCase().includes(inputTeamValue.toLowerCase()));
    const shouldShowAddTeamBtn = Boolean(
        inputTeamValue.length > 1 &&
            !teams.data?.filter((team) => team.name.toLowerCase() === inputTeamValue.toLowerCase()).length
    );

    const [newTeam, setNewTeam] = useState<TTeam | null>(null);
    const [usersOnTeam, setUsersOnTeam] = useState<TTeam["users"]>([]);

    const { shouldOpenSnackBar, popOverAnchorEl, handleOpenSnackBar, handleCloseSnackBar } = useSnackBar();

    const [disabledSaveButton, setDisableSaveButton] = useState(false);

    const handleMarkSelectedPeople = useCallback(
        (newPeople: TPerson[], meta: Pagination, selectedTeam?: TTeam) => {
            const teamsUsersIds = selectedTeam?.users.map((u) => u.id) || [];
            const newData = newPeople.map((p) => (teamsUsersIds.includes(p.id) ? { ...p, checked: true } : p));
            const checkedPeople = newData.filter((person) => person.checked);

            const allCheckedPeople = values(merge(keyBy(people.selected || [], "id"), keyBy(checkedPeople, "id")));
            const selectedPeople = allCheckedPeople.filter((person) => person.checked);

            // People that belongs to the current Team but are not in the current page
            // Adding these to selected avoid later removing a person that's not in the current page
            const peopleNotInCurrentPage = differenceBy(selectedTeam?.users, selectedPeople, "id").map((p) => ({
                id: p.id,
                name: p.name,
                checked: true,
                teams: [{ name: selectedTeam?.name || "" }],
            }));

            setPeople({
                ...people,
                data: newData,
                selected: [...selectedPeople, ...peopleNotInCurrentPage],
                meta,
            });
        },
        [people]
    );

    // --------------------------------------------------------------------------------------------
    // SELECT AND UNSELECT FUNCTIONS
    // --------------------------------------------------------------------------------------------
    const handleCheck = (checked: boolean, id: number) => {
        const newData = people.data.map((person) => (person.id === id ? { ...person, checked } : person));
        const checkedPeople = newData.filter((person) => person.checked);
        let newSelectedData = [] as TPerson[];

        if (checked) {
            newSelectedData = values(merge(keyBy(people.selected || [], "id"), keyBy(checkedPeople, "id")));
        } else {
            newSelectedData = people.selected?.filter((person) => person.id !== id);
        }

        setPeople({
            ...people,
            data: newData,
            selected: newSelectedData,
        });
    };

    const handleCheckAll = (checked: boolean) => {
        const newData = people.data.map((person) => ({ ...person, checked }));
        const checkedPeople = newData.filter((person) => person.checked);

        const newSelected = checked
            ? values(merge(keyBy(people.selected || [], "id"), keyBy(checkedPeople, "id")))
            : differenceBy(people.selected, newData, "id");

        setPeople({
            ...people,
            data: newData,
            selected: newSelected,
        });
    };

    const handleDeleteSelected = (id: number) => {
        const newData = people.data.map((person) => (person.id === id ? { ...person, checked: false } : person));
        const selectedData = people.selected.filter((person) => person.id !== id);

        setPeople({
            ...people,
            data: newData,
            selected: selectedData,
        });
    };

    const handleRemoveAllSelected = () => {
        const newData = people.data.map((person) => ({ ...person, checked: false }));

        setExpandSelected(false);
        setPeople({
            ...people,
            data: newData,
            selected: [],
        });
    };

    // ------------------------------------------------------
    // Team ADD / EDIT functions
    // ------------------------------------------------------
    const handleClickCounter = (ev: React.MouseEvent<HTMLDivElement, MouseEvent>, team: TTeam) => {
        ev.stopPropagation();
        setUsersOnTeam(team.users);
        handleOpenSnackBar(ev);
    };

    const handleCloseCounter = (event?: React.MouseEvent<HTMLElement>, reason?: "backdropClick" | "escapeKeyDown") => {
        if (event) event.stopPropagation();
        handleCloseSnackBar(event as React.MouseEvent<HTMLElement>, reason);
    };

    const handleAddNewTeam = () => {
        const newTeamObj: TTeam = {
            id: generateID(),
            label: inputTeamValue,
            name: inputTeamValue,
            checked: false,
            count: 0,
            users: [],
        };

        setNewTeam(newTeamObj);
        setInputTeamValue("");
        setMode("ADD");
    };

    const switchToEditTeamMode = (team: TTeam) => {
        setTeams({ ...teams, selected: [team] });
        setMode("EDIT");
        handleMarkSelectedPeople(people.data, people.meta, team);
    };

    const handleGoBack = () => {
        setNewTeam(null);
        setMode(undefined);
        setInputNameValue("");
        setInputTeamValue("");
        setExpandSelected(false);
        setTeams({
            ...teams,
            selected: [],
        });
        setPeople({
            ...people,
            data: people.data.map((person) => ({ ...person, checked: false })),
            selected: [],
        });
    };

    // ------------------------------------------------------
    // Save changes
    // ------------------------------------------------------
    const handleSaveChanges = async () => {
        const peopleIds = people.selected.map((people) => people.id);
        setDisableSaveButton(true);

        if (mode === "ADD" && newTeam !== null) {
            try {
                await createPaceDashboardTeams(peopleIds, [newTeam.name]);

                if (orgId) {
                    const result = await getPaceDashboardTeamsRef({ ...defaultMetaParams, page_size: 50, orgId });
                    handleSetResults<BCTeamsRefResponse>(result.data, "teams");
                }

                dispatch(
                    setToastConfig({
                        title: "Team created",
                        content: `Team ${newTeam.name} was created succesfully`,
                    })
                );
            } catch (err) {
                console.error(err);

                dispatch(
                    setToastConfig({
                        title: "Error creating Team",
                        content: `There was an error while creating ${newTeam.name}`,
                    })
                );
            }
        }

        if (mode === "EDIT" && teams.selected.length) {
            const selectedTeam = teams.selected[0];
            const selectedTeamPeopleIds = selectedTeam.users.map((user) => user.id);

            const usersToAdd = difference(peopleIds, selectedTeamPeopleIds);
            const usersToRemove = difference(selectedTeamPeopleIds, peopleIds);

            try {
                // Update Team
                if (usersToAdd.length) {
                    await createPaceDashboardTeams(usersToAdd, [selectedTeam.name]);
                }

                // Remove user unchecked
                if (usersToRemove.length) {
                    const promises: Promise<AxiosResponse<BCTeamsRequestResponse>>[] = [];
                    usersToRemove.forEach(async (id) => {
                        promises.push(deletePaceDashboardUsersFromTeams(id, selectedTeam.name));
                    });
                    await Promise.allSettled(promises);
                }

                if (orgId) {
                    const result = await getPaceDashboardTeamsRef({ ...defaultMetaParams, page_size: 50, orgId });
                    handleSetResults<BCTeamsRefResponse>(result.data, "teams");
                }

                dispatch(
                    setToastConfig({
                        title: "Team edited",
                        content: `Team ${selectedTeam.name} was edited succesfully`,
                    })
                );
            } catch (err) {
                console.error(err);

                dispatch(
                    setToastConfig({
                        title: "Error updating Team",
                        content: `There was an error while updating ${selectedTeam.name}`,
                    })
                );
            }
        }

        setDisableSaveButton(false);
    };

    // ------------------------------------------------------
    // Pagination functions
    // ------------------------------------------------------
    const handlePageChange = async (ev: React.ChangeEvent<unknown>, newPage: number) => {
        if (!orgId) return;

        const { data } = await getPaceDashboardIndividualsRef({
            ...defaultMetaParams,
            filter_pace_only: true,
            page_no: newPage,
            orgId,
        });
        handleSetResults<BCIndividualsRefResponse>(data, "individuals");
    };

    // ------------------------------------------------------
    // Data Request and Convert
    // ------------------------------------------------------
    const handleSetResults = useCallback(
        <T extends BCIndividualsRefResponse | BCTeamsRefResponse>(data: T, type: TList) => {
            const { results, meta } = data;

            if (type === "individuals") {
                const convertedIndividualsRef = convertIndividualsRef(
                    results as BCIndividualsRefResponse["results"],
                    people.selected
                );

                if (teams.selected?.length) {
                    handleMarkSelectedPeople(convertedIndividualsRef, meta);
                } else {
                    setPeople({
                        ...people,
                        status: "done",
                        data: convertedIndividualsRef,
                        meta,
                    });
                }
            }

            if (type === "teams") {
                const convertedTeamsRef = convertTeamsRef(results as BCTeamsRefResponse["results"], teams.selected);

                setTeams({
                    ...teams,
                    data: convertedTeamsRef,
                    meta,
                });
            }
        },
        [handleMarkSelectedPeople, people, teams]
    );

    // ------------------------------------------------------
    // Filter functions
    // ------------------------------------------------------
    const fetchPeople = useCallback(
        async (value: string) => {
            if (!orgId) return;

            try {
                setPeople((prevValue) => ({ ...prevValue, status: "fetching" }));
                const { data } = await getPaceDashboardIndividualsRef({
                    ...defaultMetaParams,
                    filter_pace_only: true,
                    filter_name: value,
                    orgId,
                });
                handleSetResults<BCIndividualsRefResponse>(data, "individuals");
            } catch (err) {
                setRequestStatus("error");
                console.error(err);
            }
        },
        [handleSetResults, orgId]
    );

    const handleFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        if (mode) {
            setInputNameValue(value);
            delayedHandleInputChange(value);
        } else {
            setInputTeamValue(value);
        }
    };

    const { delayedHandleInputChange } = useInputDebounce(fetchPeople);

    // Init
    useEffect(() => {
        // Fetch chart data
        const initialFetch = async () => {
            if (!orgId) return;

            const promises = [];

            if (!people.data?.length) {
                promises.push(getPaceDashboardIndividualsRef({ ...defaultMetaParams, filter_pace_only: true, orgId }));
            }

            if (!teams.data?.length) {
                promises.push(getPaceDashboardTeamsRef({ ...defaultMetaParams, page_size: 50, orgId }));
            }

            if (promises.length) {
                try {
                    setRequestStatus("fetching");

                    const response = await Promise.allSettled(promises);

                    for (const result of response) {
                        if (result.status === "rejected") {
                            return {
                                error: true,
                                reason: result.reason,
                            };
                        }

                        if (result.status === "fulfilled") {
                            if (result.value.config.url?.includes("individuals")) {
                                const data = result.value.data as BCIndividualsRefResponse;
                                handleSetResults<BCIndividualsRefResponse>(data, "individuals");
                            }

                            if (result.value.config.url?.includes("teams")) {
                                const data = result.value.data as BCTeamsRefResponse;
                                handleSetResults<BCTeamsRefResponse>(data, "teams");
                            }
                        }
                    }
                } catch (err) {
                    console.error(err);
                } finally {
                    setRequestStatus("done");
                }
            }
        };

        initialFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        people,
        teams,
        requestStatus,
        expandSelected,
        mode,
        inputNameValue,
        inputTeamValue,
        filteredTeams,
        shouldShowAddTeamBtn,
        newTeam,
        usersOnTeam,
        isSomePeopleSelected,
        isAllPeopleSelected,
        disabledSaveButton,
        setExpandSelected,
        switchToEditTeamMode,
        shouldOpenSnackBar,
        popOverAnchorEl,
        handleGoBack,
        handleFilter,
        handleAddNewTeam,
        handleCheck,
        handleCheckAll,
        handleDeleteSelected,
        handleRemoveAllSelected,
        handleSaveChanges,
        handlePageChange,
        handleClickCounter,
        handleCloseCounter,
    };
};

export default useTeamsModal;
